import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronDown, FileText, LogOut, Settings } from 'lucide-react';
import type { Session } from '@supabase/supabase-js';

import { cn } from '../../lib/cn';
import { supabase } from '../../lib/supabase';
import { logAudit } from '../../lib/audit';
import styles from './UserMenu.module.css';

interface Props {
  session: Session;
  fullName: string | null;
  role: string | null;
}

const ROLE_HE: Record<string, string> = {
  owner: 'בעלים',
  admin: 'מנהל',
  sales: 'מכירות',
  marketing: 'שיווק',
  viewer: 'צופה',
};

function initials(name: string | null, email: string | null): string {
  const trimmedName = name?.trim();
  const source = (trimmedName && trimmedName.length > 0 ? trimmedName : email) ?? '';
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase();
  }
  return source.slice(0, 2).toUpperCase();
}

export function UserMenu({ session, fullName, role }: Props) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const onLogout = async () => {
    setOpen(false);
    // Log BEFORE signOut — once the session ends, auth.uid() returns NULL
    // and the RLS audit_insert_self policy will reject the write.
    await logAudit({ action: 'auth.signed_out' });
    await supabase.auth.signOut();
    navigate('/auth/login', { replace: true });
  };

  const displayName = fullName ?? session.user.email ?? 'משתמש';
  const roleLabel = role ? (ROLE_HE[role] ?? role) : '';

  return (
    <div className={styles.wrap} ref={wrapRef}>
      <button
        type="button"
        className={styles.trigger}
        onClick={() => { setOpen((v) => !v); }}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="תפריט משתמש"
      >
        <span className={styles.avatar}>{initials(fullName, session.user.email ?? null)}</span>
        <ChevronDown size={14} className={cn(styles.chevron, open && styles.open)} />
      </button>

      {open ? (
        <div className={styles.dropdown} role="menu">
          <div className={styles.who}>
            <span className={styles.whoName}>{displayName}</span>
            {roleLabel ? <span className={styles.whoRole}>{roleLabel}</span> : null}
            <span className={styles.whoEmail}>{session.user.email}</span>
          </div>

          <Link
            to="/settings"
            className={styles.menuLink}
            role="menuitem"
            onClick={() => { setOpen(false); }}
          >
            <Settings size={16} />
            הגדרות
          </Link>

          <Link
            to="/docs"
            className={styles.menuLink}
            role="menuitem"
            onClick={() => { setOpen(false); }}
          >
            <FileText size={16} />
            מסמכים שלי
          </Link>

          <hr className={styles.divider} />

          <button
            type="button"
            className={cn(styles.menuLink, styles.logout)}
            role="menuitem"
            onClick={() => { void onLogout(); }}
          >
            <LogOut size={16} />
            התנתק
          </button>
        </div>
      ) : null}
    </div>
  );
}
